import prepareDescriptionBlocks from "@ext/markdown/elements/openApi/print/descriptionBlocks";
import OpenApiPaginator, { OpenApiSectionPaginator } from "@ext/markdown/elements/openApi/print/OpenApiPaginator";
import lineViewerCodeBlocks from "@ext/markdown/elements/openApi/print/viewerCodeBlocks";
import waitForOpenApiBlocks from "@ext/markdown/elements/openApi/print/waitForOpenApiBlocks";
import { throwIfAborted } from "@ext/print/utils/pagination/abort";
import type { PrintNodeHandler } from "@ext/print/utils/pagination/nodeHandlers";
import Paginator from "@ext/print/utils/pagination/Paginator";

/**
 * Containers whose own children are what flows.
 *
 * Every one of these is the same shape — a box that has to keep its class, its width and its type styles on
 * every page its content runs onto, around a list of children the paginator already knows how to deal out.
 * The children are ordinary content: paragraphs, lists, code blocks, headings, tables, operation cards.
 *
 * The two descriptions are on this list because a spec's Markdown is routinely longer than a page, at the
 * document's level and at a tag's alike. While they were not, each was lifted out as one indivisible run,
 * which put everything around it on a single page box: a thousand pixels under `overflow: hidden` on screen,
 * printed over the next sheet on paper, and on a tag section the tail of the description simply gone.
 *
 * By class throughout: the viewer draws itself with React, so a section is a `div.api-section` and the
 * schemas are a `div.models-section`. The custom elements this used to name do not exist any more, and a
 * `tagName` check silently returned null for every section — the block then went to paper unsplit. The two
 * `*-body` boxes are the disclosure bodies the kit renders between a section and its list of cards; the
 * viewer names them for this, and unnamed they would be placed whole with every card inside them.
 */
export const FLOWING_CONTAINERS = [
	"doc-header",
	"doc-info",
	"description",
	// Обёртка, в которой Gramax отдаёт вьюеру блочный markdown (`openApiMarkdown.tsx`): без неё нельзя —
	// `dangerouslySetInnerHTML` требует элемент, — а с ней описание становится единственным ребёнком
	// контейнера, и «раздать детей» перестаёт что-либо делить.
	"openapi-markdown",
	"doc-body",
	"api-section",
	"section-heading",
	"section-description",
	"section-body",
	"operation-stack",
	// Обёртка описания внутри карточки: `Content` вьюера. Без неё тело карточки состоит из одного
	// неделимого куска, и операция с длинным описанием не влезает в страницу — на бумаге её обрезает.
	"markdown",
	"models-section",
	"models-body",
	"models-stack",
	// The server block is the one part of the header that carries a table of its own, and a spec may declare
	// as many server variables as it likes.
	"server-block",
	"server-body",
	"server-print",
	"viewer-table",
	// Everything under an operation's description: the request, its body, the code samples and the responses.
	// Placed whole, this part of a card went to the next sheet in one piece and was cut by its edge — the
	// reference catalog lost the tail of seven operations that way and left the sheet before each one blank.
	"op-content",
	"request-block",
	"code-samples",
	"response-content",
	"response-details",
	// What the viewer prints in full instead of a switch (layout="print"): every media type, with its schema
	// and each of its examples. A schema is a table, and an example is a code pane split into lines — see
	// print/viewerCodeBlocks.ts.
	"print-contents",
	"media-variant",
	"print-schema",
	"print-example",
	"schema-box",
	"example-wrap",
];

const stackOf = (node: HTMLElement): HTMLElement | null =>
	FLOWING_CONTAINERS.some((name) => node.classList.contains(name)) ? node : null;

/**
 * A card printed open is a container around its body, and the body is a list of blocks — a description, the
 * parameters, the responses. Whole, a card taller than a page went to paper cut off at the page's edge with
 * nothing to tell the reader.
 *
 * Not a name on the list: the kit's disclosure keeps one unnamed wrapper between the card and its body, so
 * the card is the outer end of a chain and the body its inner one — `wrappersBetween` walks the wrapper in
 * between, the way it walks a section down to its stack. The head, everything in the card beside that chain,
 * opens the run once; the chain repeats on every page the body spills onto. A card drawn without a body —
 * the screen's closed one — has nothing to deal out and stays one node.
 */
const bodyOf = (node: HTMLElement): HTMLElement | null =>
	node.classList.contains("op-card") || node.classList.contains("model-card")
		? node.querySelector<HTMLElement>(":scope > * > :is(.op-body, .model-content)")
		: null;

/**
 * Hands the print pipeline the viewer's own tables in the shape it already knows.
 *
 * Parameters, responses, a schema, the list of server variables — every one of them is the kit's `Table`
 * inside `.viewer-table`, and the kit puts its own scroller between the two. `TablePaginator` splits the node
 * marked `data-component="table"` and expects the table itself to be that node's first child, which is
 * exactly what the scroller is; the box around it keeps the border and the radius and is cloned per page.
 *
 * Unmarked, a table is one indivisible node: a server with a long list of variables printed the rows that
 * fitted the page and lost the rest under `overflow: hidden`, with nothing to tell the reader.
 */
const markViewerTables = (block: HTMLElement): void => {
	for (const scroller of block.querySelectorAll<HTMLElement>(".viewer-table > div"))
		if (scroller.firstElementChild?.tagName === "TABLE") scroller.dataset.component = "table";
};

/**
 * Everything a block needs before the page it lands on is measured, and before anything decides whether it
 * fits: a wide table is scaled to the page here, and a block short enough to be placed whole never reaches
 * the paginator below — the fit check comes first, and its columns would run off the side of the sheet.
 *
 * Never claims the node. Here rather than in `waitForReady` because the block is on the page and rendered by
 * now, which is what fitting a table needs and what readiness cannot promise: a settled block is one whose
 * model is parsed, and its Markdown is written into the DOM after that.
 */
const openApiPrepareFn: PrintNodeHandler["handle"] = async (node) => {
	if (!OpenApiPaginator.contentHost(node)) return false;

	prepareDescriptionBlocks(node);
	lineViewerCodeBlocks(node);
	markViewerTables(node);
	// Heights were taken before any of that happened, and a scaled table reports the size it would have had
	// unscaled. Everything after this is measured against the page, so the block is measured again first.
	await Paginator.paginationInfo.nodeDimension.remeasure(node, Paginator.controlInfo.yieldTick, () =>
		throwIfAborted(Paginator.controlInfo.signal),
	);

	return false;
};

export const openApiPrepareHandler: PrintNodeHandler = {
	isRequired: true,
	handle: openApiPrepareFn,
};

/**
 * One handler for the OpenAPI block as a top-level article node, and for every container met inside it while
 * that block is being split. All of them are lists inside repeatable chrome — see OpenApiPaginator.
 */
const openApiHandlerFn: PrintNodeHandler["handle"] = async (node, paginator) => {
	const contentHost = OpenApiPaginator.contentHost(node);
	const stack = contentHost ?? stackOf(node) ?? bodyOf(node);
	if (!stack) return false;

	const blockPaginator = contentHost
		? new OpenApiPaginator(node, paginator)
		: new OpenApiSectionPaginator(node, stack, paginator);
	await blockPaginator.paginateNode();

	await Paginator.controlInfo.yieldTick();
	throwIfAborted(Paginator.controlInfo.signal);
	return true;
};

const openApiHandler: PrintNodeHandler = {
	handle: openApiHandlerFn,
	waitForReady: waitForOpenApiBlocks,
};

export default openApiHandler;
